import { useState, useEffect } from 'react';
import type { FC, ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { experimentalStyled } from '@mui/material/styles';
import GlobalHeaderNavbar from '../globalNav/GlobalHeaderNavbar';
import GlobalSidebarNav from '../globalNav/GlobalSidebarNav';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useParams } from 'react-router';
import useVendor from '../../hooks/useVendor';
import { useNavigate } from 'react-router-dom';
import VendorApi from '../../api/VendorApi';
import { ODataResponse } from 'src/types/oDataResponse';
import { Vendor } from 'src/types/vendor';
import useAuth from 'src/hooks/useAuth';
import ShowTour from '../ShowTour';

interface DashboardLayoutProps {
  children?: ReactNode;
}

const DashboardLayoutRoot = experimentalStyled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  height: '100%',
  overflow: 'hidden',
  width: '100%',
}));

const DashboardLayoutContainer = experimentalStyled('div')({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden',
});

const DashboardLayoutContent = experimentalStyled('div')({
  flex: '1 1 auto',
  height: '100%',
  overflow: 'auto',
  position: 'relative',
  WebkitOverflowScrolling: 'touch',
});

const DashboardLayout: FC<React.PropsWithChildren<DashboardLayoutProps>> = () => {
  let { vendorNumber } = useParams();
  let { user, hasImpersonateVendorAuth } = useAuth();
  const [isSidebarMobileOpen, setIsSidebarMobileOpen] = useState<boolean>(false);
  const { vendor, saveVendor } = useVendor();
  const navigate = useNavigate();
  const desktopDevice = useMediaQuery('(min-width:768px)');

  useEffect(() => {
    if (desktopDevice) {
      setIsSidebarMobileOpen(false);
    } else {
      // setIsSidebarMobileOpen(true);
    }
  }, [desktopDevice]);

  useEffect(() => {
    if (vendorNumber === '0' && user.vendors && user.vendors.length > 0) {
      saveVendor(user.vendors[0]);
      navigate(`/${user.vendors[0].numberString}`);
      return;
    }

    if (vendorNumber && vendorNumber != vendor?.number?.toString()) {
      //Number in the URL doesn't match the current vendor number, so we need to get the vendor

      if (hasImpersonateVendorAuth()) {
        VendorApi.getVendorByNumber(vendorNumber)
          .then((response: ODataResponse<Vendor>) => {
            if (response && response.value && response.value.length > 0) {
              saveVendor(response.value[0]);
            }
          })
          .catch(() => {
            navigate('/404');
          });
      } else {
        //If this user is not allowed to impersonate vendors, default them to their first vendor
        if (!user.vendors || user.vendors.length <= 0) {
          console.warn("User doesn't have any vendors");
          navigate('/');
          return;
        }

        let selectedVendor: Vendor = user.vendors?.find((v) => v.numberString === vendorNumber);

        if (selectedVendor && selectedVendor != null) {
          saveVendor(selectedVendor);
          navigate(`/${selectedVendor.numberString}`);
        } else {
          saveVendor(user.vendors[0]);
          navigate(`/${user.vendors[0].numberString}`);
        }
      }
    }
  }, [vendorNumber, vendor?.number, user.vendors, hasImpersonateVendorAuth, saveVendor, navigate]);

  return (
    <DashboardLayoutRoot className="dashboard-layout">
      {vendorNumber && vendorNumber !== '0' && vendorNumber !== '' && (
        <ShowTour
          id="dashboardNavTour"
          steps={[
            {
              target: '#global-header__menu__item__vendor-select',
              content: 'Are you a manufacturing rep group? Navigate between vendor booths by clicking here.',
              disableBeacon: true,
              locale: { skip: 'Close Tour' },
            },
            {
              target: '#global-nav__item-link__vendorBooth',
              content: 'Manage your virtual booth content.',
              disableBeacon: true,
              locale: { skip: 'Close Tour' },
            },
            {
              target: '#global-nav__item-link__userAdmin',
              content: 'Add or edit portal users.',
              disableBeacon: true,
              locale: { skip: 'Close Tour' },
            },
            {
              target: '#global-header__account-pop-over',
              content: 'Retake the tour, find your BFG Category Manager or logout.',
              disableBeacon: true,
              locale: { skip: 'Close Tour' },
            },
          ]}
        />
      )}
      <GlobalHeaderNavbar onSidebarMobileOpen={(): void => setIsSidebarMobileOpen(true)} />
      <GlobalSidebarNav onMobileClose={(): void => setIsSidebarMobileOpen(false)} openMobile={isSidebarMobileOpen} />
      <DashboardLayoutContainer>
        <DashboardLayoutContent className="dashboard-layout-content">
          <Outlet />
        </DashboardLayoutContent>
      </DashboardLayoutContainer>
    </DashboardLayoutRoot>
  );
};

export default DashboardLayout;
